import {
  getSupabaseQueryErrorMessage,
  getUserFacingErrorMessage,
  isTransientInfrastructureError,
} from '@/lib/errors'

export type ConnectivityKind = 'offline' | 'transient' | 'server' | 'slow'

export function getBrowserIsOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

export function classifyConnectivityError(error: unknown): ConnectivityKind {
  if (!getBrowserIsOnline()) return 'offline'
  if (isTransientInfrastructureError(error)) return 'transient'
  return 'server'
}

export function getConnectivityErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  switch (classifyConnectivityError(error)) {
    case 'offline':
      return 'You appear to be offline. Reconnect to load the latest data.'
    case 'transient':
      if (/timeout|timed out|abort/i.test(getSupabaseQueryErrorMessage(error, ''))) {
        return 'The request timed out on a slow connection. Try again when the network improves, or continue with the last loaded data.'
      }
      return 'The connection is slow or unstable. You can retry, or keep working with the last loaded data.'
    default:
      return getSupabaseQueryErrorMessage(error, fallbackMessage)
  }
}

export function getConnectivityAlertTitle(kind: ConnectivityKind, resourceLabel: string): string {
  switch (kind) {
    case 'offline':
      return 'You are offline'
    case 'slow':
      return `Loading ${resourceLabel} is taking longer than expected`
    case 'transient':
      return `Could not refresh ${resourceLabel}`
    default:
      return `Could not load ${resourceLabel}`
  }
}

export function getSlowFetchNoticeCopy(resourceLabel: string): {
  title: string
  description: string
} {
  return {
    title: getConnectivityAlertTitle('slow', resourceLabel),
    description: 'Your connection may be slow. Please wait while we load the latest data.',
  }
}

export function getMutationFailureToast(
  error: unknown,
  actionPhrase: string
): { title: string; description: string } {
  const kind = classifyConnectivityError(error)
  if (kind === 'offline') {
    return {
      title: 'You are offline',
      description: `Reconnect before you ${actionPhrase}.`,
    }
  }
  if (kind === 'transient') {
    return {
      title: 'Connection problem',
      description: `Could not ${actionPhrase}. Check your connection and try again.`,
    }
  }
  const message = getUserFacingErrorMessage(error, 'Please try again.')
  return {
    title: `Could not ${actionPhrase}`,
    description: message,
  }
}
